import type { TFunction } from 'i18next'
import { Plus, Trash2 } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Field, Select } from '@/components/ui/Field'
import type { ChoiceOption } from '@/components/ui/control'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { useCaseTranches } from '@/features/finance/api/useFinance'
import { normaliseDecimal } from '@/features/finance/lines'
import { cn } from '@/lib/cn'
import { formatDate, formatMoney, formatNumber } from '@/lib/format'
import type { AllocationTargetKind, CaseTrancheRow, Payment } from '@/types/domain'

/* Saying what a payment settles (AD-11).
 *
 * Allocation is EXPLICIT — never inferred by date, never FIFO, and not
 * "suggested" either. A pre-filled split that the user only has to confirm is
 * the same failure with a friendlier name: the platform would still have
 * guessed which invoice was paid, and the guess would still be wrong on the
 * case where it matters. So this sheet starts empty and every figure in it is
 * one somebody typed.
 *
 * It is a CONFIRMED-WRITE flow end to end (UX-DR-24). Nothing appears in the
 * allocation list until the server has accepted the whole request: the
 * conservation checks run under row locks against sums another Finance user may
 * be moving in the same instant, so a row drawn before the answer arrives is a
 * receivable the screen calls settled and the ledger does not.
 *
 * THE PICKER, and why it replaced a typed identifier. This sheet used to take
 * the target id by hand, because nothing on the server could resolve an
 * allocation target at all — `Service.allocationSource` answered "not ok" for
 * every kind, so every id, typed perfectly, came back
 * `ErrAllocationTargetUnavailable`. That is no longer true:
 * `WithTrancheAllocations` registers a source for `tranche`, so exactly one kind
 * resolves and the sheet offers exactly that one.
 *
 * A uuid typed into a text box was never a form somebody could fill correctly —
 * there is no screen in the product that shows a tranche's id — so the field was
 * only ever honest as a placeholder for this. The KIND select went with it: with
 * one resolvable kind it was a control with one option, and `invoice` and
 * `debit_note` are dead ends until Story 5.5 lets an invoice leave `draft` and
 * 5.6 gives a debit note a table. When either registers, the kind returns as a
 * second dimension of this same picker.
 *
 * What it CANNOT do, stated because the omission is the server's shape rather
 * than a shortcut here: `TrancheDTO` carries no settled or outstanding figure, so
 * a tranche already paid in full still appears in the list. The server refuses
 * the over-allocation under its row lock either way (AD-11) — what is missing is
 * discoverability, not the control, and closing it means widening the DTO.
 */

/** One row of the split editor. */
export interface AllocationSplitDraft {
  /** React's, never the wire's — stable across edits so a row above can be
   *  retyped without the focus jumping out of the row being edited. */
  key: string
  targetKind: AllocationTargetKind
  targetId: string
  amount: string
}

/** The one kind a target source resolves today (`WithTrancheAllocations`).
 *
 *  A constant rather than a picker's value: the wire still carries the kind on
 *  every split, so the field has to exist, but nothing on this screen chooses
 *  it. Epic 5 turns this back into a dimension of the picker. */
const RESOLVABLE_KIND: AllocationTargetKind = 'tranche'

/** Targets supplied by the caller instead of the case's receivable tranches.
 *
 *  The direction page's «Оплатить перевозчику» is what needs this (issue #35
 *  story 6): an OUTBOUND payment settles a cost line, and no tranche is a thing
 *  it can pay. The alternative was a second allocation sheet, which is a second
 *  place the conservation rule, the ceiling hint and the confirmed-write
 *  discipline would have to stay right.
 *
 *  A whole list rather than a filter or a kind flag, deliberately: resolving
 *  what a target is called and what it is worth needs a table this sheet has no
 *  business reading. The caller already holds it — the leg's money block — and
 *  passing the resolved options keeps this component a picker over choices
 *  rather than a second reader of somebody else's rows.
 *
 *  Absent means the sheet behaves exactly as it always has, which is the point:
 *  the Money page's call site is unchanged and its tranche read is not even
 *  issued when this is supplied. */
export interface AllocationTargets {
  kind: AllocationTargetKind
  options: ChoiceOption[]
  /** The standing sentence under the picker — what these targets ARE. Not an
   *  error: nothing has gone wrong, so nothing about it is red. */
  notice?: string
  /** What to say when the list is EMPTY, which is a different fact from what
   *  the list is.
   *
   *  Supplied by the caller because the answer depends on what was being
   *  offered: `targetsNone` sends the reader to agree a rate so tranches get
   *  materialised, which is exactly the wrong advice in front of somebody
   *  paying a haulier — no receivable of theirs is involved. Absent falls back
   *  to that receivable wording, which is right for a caller that had tranche-
   *  shaped targets and none of them. */
  emptyNotice?: string
}

/** Accepts `1234.56`, `1 234,56` and `1,234.56`; rejects text and blanks.
 *
 *  A near-twin of the payment sheet's own check, and deliberately not shared
 *  with it: a component file may export only components (react-refresh), and a
 *  four-line regex is a poorer reason to add a module than the confusion of
 *  importing form validation from a file full of JSX. */
function isValidAmount(raw: string): boolean {
  const normalised = normaliseDecimal(raw)
  return normalised !== '' && /^\d+(\.\d{1,2})?$/.test(normalised)
}

function isSplitComplete(split: AllocationSplitDraft): boolean {
  return (
    split.targetId.trim() !== '' &&
    isValidAmount(split.amount) &&
    Number(normaliseDecimal(split.amount)) > 0
  )
}

function emptySplit(key: string, kind: AllocationTargetKind): AllocationSplitDraft {
  // Blank, not pre-filled with the unallocated balance, and no target chosen
  // even when the case has exactly one. Offering the whole remaining amount, or
  // pre-selecting the only tranche, is a guess about what this money settles —
  // the one thing AD-11 forbids the platform from making. A single-option picker
  // still costs one click, and that click is the record that a human decided.
  return { key, targetKind: kind, targetId: '', amount: '' }
}

/** One tranche as the picker offers it: what it is, what it is worth, and when
 *  it was expected.
 *
 *  The SEQUENCE is the label, never the array index — `tranches_dto.go` says so
 *  outright, and "the first tranche" is what Operations chase. The amount and the
 *  date go in the hint rather than the label so the list stays scannable while
 *  still carrying enough to tell two tranches of one case apart, which on a 50-50
 *  case is exactly the date and nothing else.
 *
 *  A tranche with no expected date says so. `expectedDate: null` is a value —
 *  "nothing agreed yet, so not chaseable" — and rendering it as a blank would
 *  make the one tranche somebody still has to date look like the others. */
function trancheOption(
  tranche: CaseTrancheRow,
  total: number,
  t: TFunction,
  formatted: string,
): ChoiceOption {
  return {
    value: tranche.id,
    label: t('finance.allocations.trancheLabel', {
      sequence: tranche.sequence,
      total,
    }),
    hint: t('finance.allocations.trancheHint', {
      amount: formatMoney(tranche.amount, tranche.currency),
      when: formatted,
    }),
  }
}

export function PaymentAllocationSheet({
  open,
  onClose,
  payment,
  onSubmit,
  pending,
  /** What the SERVER refused, if it did — an over-allocation naming the ceiling
   *  it breached, or a target kind nothing has registered yet. Shown over the
   *  local hint: the server's answer is the more urgent news, and the fields it
   *  names may all look valid from here. */
  submitError,
  /** Targets from the caller rather than the case's tranches — see
   *  `AllocationTargets`. The receivable behaviour is unchanged without it. */
  targets,
}: {
  open: boolean
  onClose: () => void
  payment: Payment
  onSubmit: (splits: AllocationSplitDraft[]) => void
  pending?: boolean
  submitError?: string
  targets?: AllocationTargets
}) {
  const { t } = useTranslation()
  const kind = targets?.kind ?? RESOLVABLE_KIND
  const [draft, setDraft, touched, markTouched] = useFormDraft<AllocationSplitDraft[]>(
    // Keyed on the payment, so opening the sheet on a different payment starts
    // a fresh split rather than carrying the last one's rows across.
    open ? payment.id : null,
    () => [emptySplit('split-1', kind)],
  )

  /* The targets, from the payment's OWN case. A payment always belongs to one
     (`Payment.caseId`), and a tranche of another case is not a thing this money
     can settle — the server refuses it outright (`ErrAllocationTargetNotOnCase`,
     which no foreign key catches), so scoping the picker to the case is the same
     rule stated where somebody can act on it rather than after a refusal.

     NOT ASKED AT ALL when the caller supplied its own targets: the hook is
     disabled by an empty id, so a payables allocation costs no request for a
     receivable list nothing on that sheet can offer. */
  const tranches = useCaseTranches(targets ? '' : payment.caseId)

  const targetOptions = useMemo(() => {
    if (targets) return targets.options

    const rows = tranches.data ?? []
    return rows.map((tranche) =>
      trancheOption(
        tranche,
        rows.length,
        t,
        /* `formatDate`, the pure locale-independent formatter, rather than
           `useFormat().date`: the money page formats every date this way
           (UX-DR-30 — one format across both languages), and the hook reaches
           the session through a query, which would make a form sheet depend on
           the signed-in user to render a date it prints identically either way. */
        tranche.expectedDate === null
          ? t('finance.allocations.trancheNoDate')
          : formatDate(tranche.expectedDate),
      ),
    )
  }, [targets, tranches.data, t])

  const currency = payment.amount.currency
  const unallocated = Number(payment.unallocated.amount)
  const requested = draft.reduce(
    (sum, split) => sum + (isSplitComplete(split) ? Number(normaliseDecimal(split.amount)) : 0),
    0,
  )

  /* The client-side ceiling check. It exists to spare a round trip and to put
     the number beside the fields that produced it — NOT as the guarantee. The
     guarantee is the server's, taken under `FOR UPDATE` against sums that may
     have moved since this sheet was opened (AD-11, AC 3), and a client that
     treated its own arithmetic as authoritative would be exactly the
     check-then-write race that acceptance criterion names. */
  const overAllocated = requested > unallocated
  const incomplete = draft.length === 0 || !draft.every(isSplitComplete)

  /* Nothing to allocate against is a legal state, not a fault: a case registered
     before its rate was agreed (FR-70, and every migrated case) has no tranches,
     and `RegisterCaseInTx` materialises them only when a rate was stated. Blocking
     the submit is what stops the sheet accepting a split it knows nothing can
     resolve — the same refusal the server would give, one round trip earlier. */
  /* Supplied targets are ALREADY resolved — there is no read to be pending or
     to have failed, so the only question left is whether the list is empty. */
  const targetsReady = targets !== undefined || tranches.isSuccess
  const noTargets = targetsReady && targetOptions.length === 0
  const invalid = incomplete || overAllocated || noTargets || !targetsReady

  const localError = touched
    ? overAllocated
      ? t('finance.allocations.exceedsBalance', {
          balance: formatMoney(payment.unallocated.amount, currency),
        })
      : incomplete
        ? t('finance.allocations.splitsRequired')
        : undefined
    : undefined

  /* The picker's own state, which is NOT the same question as the draft's
     validity and must not be reported as an error: a list still loading is not a
     mistake anybody made, and a case with no tranches is a fact about the case.
     Only a failed READ is red — it means the reader is looking at a picker that
     may be missing options rather than at a case that has none. */
  const targetNotice = targets
    ? noTargets
      ? (targets.emptyNotice ?? t('finance.allocations.targetsNone'))
      : targets.notice
    : tranches.isError
      ? undefined
      : tranches.isPending
        ? t('finance.allocations.targetsLoading')
        : noTargets
          ? t('finance.allocations.targetsNone')
          : t('finance.allocations.targetsTrancheOnly')

  const update = (key: string, patch: Partial<AllocationSplitDraft>) =>
    setDraft(draft.map((split) => (split.key === key ? { ...split, ...patch } : split)))

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('finance.allocations.title', { reference: payment.reference })}
      submitLabel={t('finance.allocations.submit')}
      pending={pending}
      // A standing limitation, not a failure: nothing has gone wrong, so nothing
      // here is red. Today it names the one kind that resolves; when Epic 5
      // registers invoices it has one fewer thing to explain.
      notice={targetNotice}
      /* Three sources, one slot, in order of what the reader can do about it: the
         server's refusal first because it describes a request that has already
         been made, then a failed target read because it means the picker below is
         not to be trusted, then the local hint. */
      error={
        submitError ??
        (tranches.isError ? t('finance.allocations.targetsFailed') : localError)
      }
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      {/* The balance being spent, stated before the fields that spend it.
          Unallocated cash is legal (AD-11) — this figure is what makes it
          visible, and what the splits below are checked against. */}
      <div className="bg-surface-sunken flex flex-wrap gap-x-6 gap-y-2 rounded-[var(--radius-md)] p-3">
        <span className="flex flex-col gap-1">
          <span className="asa-eyebrow">{t('common.fields.amount')}</span>
          <span className="asa-num text-text-body text-[14px]">
            {formatMoney(payment.amount.amount, currency)}
          </span>
        </span>
        <span className="flex flex-col gap-1">
          <span className="asa-eyebrow">{t('finance.payments.allocated')}</span>
          <span className="asa-num text-text-body text-[14px]">
            {formatMoney(payment.allocated.amount, currency)}
          </span>
        </span>
        <span className="flex flex-col gap-1">
          <span className="asa-eyebrow">{t('finance.payments.unallocated')}</span>
          <span
            className={cn(
              'asa-num text-[14px] font-semibold',
              unallocated > 0 ? 'text-attention' : 'text-text-muted',
            )}
          >
            {formatMoney(payment.unallocated.amount, currency)}
          </span>
        </span>
      </div>

      {draft.map((split, index) => {
        const flagged = touched && !isSplitComplete(split)
        return (
          <div
            key={split.key}
            className={cn(
              'bg-surface-sunken border-line-hair flex flex-col gap-2.5 rounded-[var(--radius-md)] border p-2.5',
              flagged && 'border-negative/45',
            )}
          >
            <div className="flex items-start gap-2">
              {/* The target, as a picker over the case's own tranches. Searchable
                  is deliberately OFF: a series is two or three rows on the cases
                  this company runs, and a filter box over three options is a
                  control that only ever gets in the way. */}
              <Field
                label={t('finance.allocations.target')}
                required
                className="flex-1"
                error={
                  flagged && split.targetId === ''
                    ? t('finance.allocations.targetRequired')
                    : undefined
                }
              >
                {(props) => (
                  <Select
                    value={split.targetId}
                    onChange={(targetId) => update(split.key, { targetId })}
                    placeholder={t('finance.allocations.targetPlaceholder')}
                    options={targetOptions}
                    aria-label={t('finance.allocations.targetN', { n: index + 1 })}
                    {...props}
                  />
                )}
              </Field>

              {/* Destructive, and icon-only for the reason the charge editor's
                  remove is: a labelled button per row would crowd out the
                  fields, and a removed split is undone by choosing it again. */}
              <button
                type="button"
                onClick={() => setDraft(draft.filter((row) => row.key !== split.key))}
                aria-label={t('finance.allocations.remove', { n: index + 1 })}
                title={t('finance.allocations.remove', { n: index + 1 })}
                className={cn(
                  'text-text-faint hover:text-negative hover:bg-negative-soft mt-[22px]',
                  'flex size-9 shrink-0 cursor-pointer items-center justify-center',
                  'rounded-[var(--radius-md)] transition-colors',
                )}
              >
                <Trash2 size={15} aria-hidden />
              </button>
            </div>

            {/* The split is in the PAYMENT's currency, which is why the code is
                shown and not chosen: money coming out of this balance cannot be
                denominated in something else, and offering a picker would
                invite a split that the balance it draws on cannot express. */}
            <Field
              label={t('common.fields.amount')}
              required
              hint={t('finance.allocations.amountHint', { currency })}
            >
              {(props) => (
                <Input
                  inputMode="decimal"
                  value={split.amount}
                  onChange={(e) => update(split.key, { amount: e.target.value })}
                  aria-label={t('finance.allocations.amountN', { n: index + 1 })}
                  className="asa-num"
                  {...props}
                />
              )}
            </Field>
          </div>
        )
      })}

      <div className="flex items-center justify-between gap-2 pt-0.5">
        <Button
          variant="secondary"
          size="sm"
          iconLeft={Plus}
          onClick={() =>
            setDraft([
              ...draft,
              // The index alone would be reused after a removal, and React
              // would keep the removed row's DOM node and its cursor.
              emptySplit(
                `split-${draft.length}-${Math.random().toString(36).slice(2, 8)}`,
                kind,
              ),
            ])
          }
        >
          {t('finance.allocations.add')}
        </Button>

        {/* The running total of what is about to be applied, beside the balance
            it is checked against. Display arithmetic only — the server recounts
            it under lock and its answer is the one that decides. */}
        {draft.length > 0 ? (
          <span className="flex items-baseline gap-2">
            <span className="asa-eyebrow">{t('finance.allocations.requested')}</span>
            <span
              className={cn(
                'asa-num text-[15px] font-semibold',
                overAllocated ? 'text-negative' : 'text-text-heading',
              )}
            >
              {formatNumber(String(requested), 2)}
              <span className="text-text-muted ml-1 text-[12px] font-normal">
                {currency}
              </span>
            </span>
          </span>
        ) : null}
      </div>
    </FormSheet>
  )
}
